import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import AdminLayout from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    listAdminCategories,
    createAdminCategory,
    updateAdminCategory,
    deleteAdminCategory,
} from "@/lib/api";
import { Plus, Pencil, Trash2, Loader2, Check, X } from "lucide-react";
import { toast } from "sonner";

const Categories = () => {
    const queryClient = useQueryClient();
    const [form, setForm] = useState({ id: "", label: "", sort_order: 0, active: true });
    const [editId, setEditId] = useState(null);

    const { data: categories = [], isLoading } = useQuery({
        queryKey: ["admin", "categories"],
        queryFn: listAdminCategories,
    });

    const createMutation = useMutation({
        mutationFn: createAdminCategory,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin", "categories"] });
            queryClient.invalidateQueries({ queryKey: ["categories"] });
            toast.success("Catégorie créée.");
            setForm({ id: "", label: "", sort_order: 0, active: true });
        },
        onError: (err) => toast.error(err.response?.data?.detail || "Erreur."),
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => updateAdminCategory(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin", "categories"] });
            queryClient.invalidateQueries({ queryKey: ["categories"] });
            toast.success("Catégorie mise à jour.");
            setEditId(null);
        },
        onError: (err) => toast.error(err.response?.data?.detail || "Erreur."),
    });

    const deleteMutation = useMutation({
        mutationFn: deleteAdminCategory,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin", "categories"] });
            queryClient.invalidateQueries({ queryKey: ["categories"] });
            toast.success("Catégorie supprimée.");
        },
        onError: () => toast.error("Impossible de supprimer la catégorie."),
    });

    const handleCreate = (e) => {
        e.preventDefault();
        createMutation.mutate(form);
    };

    const startEdit = (cat) => {
        setEditId(cat.id);
        setForm({ ...cat });
    };

    const handleUpdate = (e) => {
        e.preventDefault();
        updateMutation.mutate({ id: editId, data: form });
    };

    const sorted = [...categories].sort((a, b) => a.sort_order - b.sort_order);

    return (
        <AdminLayout>
            <div className="space-y-8">
                <div>
                    <h1 className="font-display text-3xl font-semibold tracking-tight">
                        Catégories
                    </h1>
                    <p className="text-neutral-500 mt-1">
                        Gérez les catégories du catalogue.
                    </p>
                </div>

                <form
                    onSubmit={editId ? handleUpdate : handleCreate}
                    className="bg-white rounded-xl border border-neutral-200 p-5 space-y-4"
                >
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                        <div className="space-y-1.5">
                            <Label className="text-sm">ID</Label>
                            <Input
                                value={form.id}
                                onChange={(e) => setForm((f) => ({ ...f, id: e.target.value }))}
                                placeholder="rotative"
                                disabled={!!editId}
                                required
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-sm">Label</Label>
                            <Input
                                value={form.label}
                                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                                placeholder="Instrumentation Rotative"
                                required
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-sm">Ordre</Label>
                            <Input
                                type="number"
                                value={form.sort_order}
                                onChange={(e) =>
                                    setForm((f) => ({ ...f, sort_order: parseInt(e.target.value, 10) || 0 }))
                                }
                            />
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                                <Checkbox
                                    id="active"
                                    checked={form.active}
                                    onCheckedChange={(v) =>
                                        setForm((f) => ({ ...f, active: !!v }))
                                    }
                                />
                                <Label htmlFor="active" className="text-sm cursor-pointer">
                                    Active
                                </Label>
                            </div>
                            <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                                {editId ? (
                                    <>
                                        <Check className="w-4 h-4 mr-2" />
                                        Mettre à jour
                                    </>
                                ) : (
                                    <>
                                        <Plus className="w-4 h-4 mr-2" />
                                        Ajouter
                                    </>
                                )}
                            </Button>
                            {editId && (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={() => {
                                        setEditId(null);
                                        setForm({ id: "", label: "", sort_order: 0, active: true });
                                    }}
                                >
                                    <X className="w-4 h-4" />
                                </Button>
                            )}
                        </div>
                    </div>
                </form>

                <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Ordre</TableHead>
                                <TableHead>ID</TableHead>
                                <TableHead>Label</TableHead>
                                <TableHead>Active</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={5} className="text-center py-8">
                                        <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#0a84ff]" />
                                    </TableCell>
                                </TableRow>
                            ) : sorted.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} className="text-center py-8 text-neutral-500">
                                        Aucune catégorie.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                sorted.map((c) => (
                                    <TableRow key={c.id}>
                                        <TableCell>{c.sort_order}</TableCell>
                                        <TableCell className="font-mono text-sm">{c.id}</TableCell>
                                        <TableCell className="font-medium">{c.label}</TableCell>
                                        <TableCell>{c.active ? "Oui" : "Non"}</TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <Button size="icon" variant="ghost" onClick={() => startEdit(c)}>
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    className="text-red-600 hover:text-red-700"
                                                    onClick={() => deleteMutation.mutate(c.id)}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </AdminLayout>
    );
};

export default Categories;
